import { asNumber, asString, asStringArray, isEmptyValue, numericValue, valueToText, type ValueContext } from './fields.ts';
import type { CollectionData, FieldDef, FilterRule, RecordData, SortRule, ViewDef } from './model.ts';
import { SYSTEM_KEYS } from './model.ts';
import { compareOrder } from './order.ts';

const collator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });

export const fold = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/* ----------------------------------------------------------------------------------------------
 * Sorting
 * -------------------------------------------------------------------------------------------- */

function compareBy(rule: SortRule, fields: Map<string, FieldDef>, ctx: ValueContext) {
  const dir = rule.dir === 'desc' ? -1 : 1;
  if (rule.fieldId === SYSTEM_KEYS.title) return (a: RecordData, b: RecordData) => dir * collator.compare(a.title, b.title);
  if (rule.fieldId === SYSTEM_KEYS.created) return (a: RecordData, b: RecordData) => dir * (a.createdAt - b.createdAt);
  if (rule.fieldId === SYSTEM_KEYS.updated) return (a: RecordData, b: RecordData) => dir * (a.updatedAt - b.updatedAt);
  const field = fields.get(rule.fieldId);
  if (!field) return () => 0;

  if (field.type === 'select') {
    // Sort by option position (e.g. Backlog → Done, Common → Legendary), empty last.
    const rank = new Map((field.config.options ?? []).map((o, i) => [o.id, i]));
    return (a: RecordData, b: RecordData) => {
      const ra = rank.get(asString(a.values[field.id]));
      const rb = rank.get(asString(b.values[field.id]));
      if (ra === undefined || rb === undefined) return ra === rb ? 0 : ra === undefined ? 1 : -1;
      return dir * (ra - rb);
    };
  }
  const numeric = ['number', 'rating', 'range', 'dice', 'formula', 'votes', 'checkbox'].includes(field.type);
  if (numeric) {
    return (a: RecordData, b: RecordData) => {
      const na = numericValue(a, field, ctx);
      const nb = numericValue(b, field, ctx);
      if (na === null || nb === null) return na === nb ? 0 : na === null ? 1 : -1;
      return dir * (na - nb);
    };
  }
  return (a: RecordData, b: RecordData) => {
    const ta = valueToText(a, field, ctx);
    const tb = valueToText(b, field, ctx);
    if (!ta || !tb) return ta === tb ? 0 : !ta ? 1 : -1;
    return dir * collator.compare(ta, tb);
  };
}

export function sortRecords(records: readonly RecordData[], rules: readonly SortRule[], fields: readonly FieldDef[], ctx: ValueContext = {}): RecordData[] {
  const byId = new Map(fields.map((f) => [f.id, f]));
  const cmps = rules.map((r) => compareBy(r, byId, { ...ctx, fields }));
  return [...records].sort((a, b) => {
    for (const cmp of cmps) {
      const c = cmp(a, b);
      if (c) return c;
    }
    return compareOrder(a, b);
  });
}

/* ----------------------------------------------------------------------------------------------
 * Filtering
 * -------------------------------------------------------------------------------------------- */

function matchRule(record: RecordData, rule: FilterRule, field: FieldDef | undefined, ctx: ValueContext): boolean {
  if (rule.fieldId === SYSTEM_KEYS.title) {
    const t = fold(record.title);
    const q = fold(asString(rule.value));
    switch (rule.op) {
      case 'contains': return t.includes(q);
      case 'notContains': return !t.includes(q);
      case 'is': return t === q;
      case 'isNot': return t !== q;
      case 'isEmpty': return !t;
      case 'isNotEmpty': return !!t;
      default: return true;
    }
  }
  if (!field) return true;
  const raw = record.values[field.id];
  switch (rule.op) {
    case 'isEmpty':
      return field.type === 'formula' || field.type === 'votes' ? numericValue(record, field, ctx) === null : isEmptyValue(field, raw);
    case 'isNotEmpty':
      return field.type === 'formula' || field.type === 'votes' ? numericValue(record, field, ctx) !== null : !isEmptyValue(field, raw);
    case 'checked':
      return raw === true;
    case 'unchecked':
      return raw !== true;
    case 'contains':
    case 'notContains': {
      const hit = fold(valueToText(record, field, ctx)).includes(fold(asString(rule.value)));
      return rule.op === 'contains' ? hit : !hit;
    }
    case 'hasAny':
    case 'hasAll':
    case 'hasNone': {
      const have = new Set(asStringArray(raw));
      const want = asStringArray(rule.value);
      if (!want.length) return true;
      if (rule.op === 'hasAny') return want.some((w) => have.has(w));
      if (rule.op === 'hasAll') return want.every((w) => have.has(w));
      return !want.some((w) => have.has(w));
    }
    case 'before':
    case 'after': {
      const d = asString(raw);
      const ref = asString(rule.value);
      if (!d || !ref) return false;
      return rule.op === 'before' ? d < ref : d > ref;
    }
    case 'is':
    case 'isNot': {
      let equal: boolean;
      if (field.type === 'select') equal = asString(raw) === asString(rule.value);
      else if (field.type === 'date') equal = asString(raw).slice(0, 10) === asString(rule.value).slice(0, 10);
      else if (['number', 'rating', 'range', 'dice', 'formula', 'votes'].includes(field.type)) {
        equal = numericValue(record, field, ctx) === asNumber(rule.value);
      } else equal = fold(valueToText(record, field, ctx)) === fold(asString(rule.value));
      return rule.op === 'is' ? equal : !equal;
    }
    case 'gt':
    case 'gte':
    case 'lt':
    case 'lte': {
      const n = numericValue(record, field, ctx);
      const ref = asNumber(rule.value);
      if (n === null || ref === null) return false;
      return rule.op === 'gt' ? n > ref : rule.op === 'gte' ? n >= ref : rule.op === 'lt' ? n < ref : n <= ref;
    }
  }
}

/** Rules whose value is still blank (while the user is editing) are ignored. */
export function isActiveRule(rule: FilterRule): boolean {
  if (['isEmpty', 'isNotEmpty', 'checked', 'unchecked'].includes(rule.op)) return true;
  const v = rule.value;
  if (Array.isArray(v)) return v.length > 0;
  return v !== undefined && v !== null && v !== '';
}

export function filterRecords(
  records: readonly RecordData[],
  rules: readonly FilterRule[],
  mode: 'and' | 'or',
  fields: readonly FieldDef[],
  ctx: ValueContext = {},
): RecordData[] {
  const active = rules.filter(isActiveRule);
  if (!active.length) return [...records];
  const byId = new Map(fields.map((f) => [f.id, f]));
  const c = { ...ctx, fields };
  return records.filter((r) =>
    mode === 'and' ? active.every((rule) => matchRule(r, rule, byId.get(rule.fieldId), c)) : active.some((rule) => matchRule(r, rule, byId.get(rule.fieldId), c)),
  );
}

/** Quick search across the title and every text-like field. */
export function searchRecords(records: readonly RecordData[], query: string, fields: readonly FieldDef[], ctx: ValueContext = {}): RecordData[] {
  const q = fold(query.trim());
  if (!q) return [...records];
  const terms = q.split(/\s+/);
  const c = { ...ctx, fields };
  return records.filter((r) => {
    const hay = fold([r.title, ...fields.map((f) => valueToText(r, f, c))].join(' '));
    return terms.every((t) => hay.includes(t));
  });
}

/** Everything a view needs, in order: search → filters → sort. */
export function queryView(
  records: readonly RecordData[],
  collection: CollectionData,
  view: Pick<ViewDef, 'filters' | 'filterMode' | 'sort'>,
  search = '',
  ctx: ValueContext = {},
): RecordData[] {
  const fields = collection.fields;
  let out = searchRecords(records, search, fields, ctx);
  out = filterRecords(out, view.filters, view.filterMode, fields, ctx);
  return sortRecords(out, view.sort, fields, ctx);
}

/* ----------------------------------------------------------------------------------------------
 * Grouping (boards)
 * -------------------------------------------------------------------------------------------- */

export interface Group {
  /** Option id / person id / 'true' / 'false', or null for "No value". */
  key: string | null;
  records: RecordData[];
}

/**
 * Group records by a select / person / checkbox field. Groups follow the option order and always
 * include every option (empty columns are useful drop targets); the "empty" group comes first.
 */
export function groupRecords(records: readonly RecordData[], field: FieldDef, personIds: readonly string[] = []): Group[] {
  const keys: (string | null)[] =
    field.type === 'select'
      ? [null, ...(field.config.options ?? []).map((o) => o.id)]
      : field.type === 'checkbox'
        ? ['false', 'true']
        : [null, ...personIds];
  const groups = new Map<string | null, RecordData[]>(keys.map((k) => [k, []]));
  for (const r of records) {
    const raw = r.values[field.id];
    let key: string | null;
    if (field.type === 'checkbox') key = raw === true ? 'true' : 'false';
    else if (field.type === 'person') key = asStringArray(raw)[0] ?? null;
    else key = typeof raw === 'string' && raw ? raw : null;
    if (!groups.has(key)) {
      // Value points at a deleted option / unknown person → treat as empty.
      key = field.type === 'person' && key ? key : null;
      if (!groups.has(key)) groups.set(key, []);
    }
    groups.get(key)!.push(r);
  }
  return [...groups].map(([key, recs]) => ({ key, records: recs }));
}

/* ----------------------------------------------------------------------------------------------
 * Aggregates (table footer, overview charts)
 * -------------------------------------------------------------------------------------------- */

export interface NumberStats {
  count: number;
  sum: number;
  min: number;
  max: number;
  avg: number;
}

export function numberStats(records: readonly RecordData[], field: FieldDef, ctx: ValueContext = {}): NumberStats | null {
  let count = 0;
  let sum = 0;
  let min = Infinity;
  let max = -Infinity;
  for (const r of records) {
    const n = numericValue(r, field, ctx);
    if (n === null) continue;
    count++;
    sum += n;
    if (n < min) min = n;
    if (n > max) max = n;
  }
  return count ? { count, sum, min, max, avg: sum / count } : null;
}
